import React from 'react';
import {FormHelperText, FormControlLabel, Checkbox as MuiCheckbox, Typography} from '@mui/material';
import {alpha, useTheme} from '@mui/material/styles';
import {FormattedMessage, useIntl} from 'react-intl';
import moment from 'moment';
import isEqual from 'react-fast-compare';
import {Button, Dialog, SegmentedControl} from 'wobi-web-common/dist/v2/index.js';
import {CoverageExtensionRow} from './CoverageExtensionRow';
import {DeviceFields} from './DeviceFields';
import TripDatePicker from './TripDatePicker';
import {Ic} from './IconBox';
import {RIDER_DISPLAY} from '../config/riderDisplay';
import {fireGtmEvent} from '../utils/marketing.utils';
import {toIsoDateValue} from '../utils/dateHelpers';
import {getRiderCoverageValues} from '../utils/riderCoverageValues';
import {
  selectExclusiveTraveler,
  updateAllTravelers,
  updateNestedTraveler,
  updateTraveler,
} from '../steps/tabs-contents/initial-details/helpers/Tab4_helpers/travelerUpdates';

const toCoverageSelection = (values) => values ? {
  locateAndExtract: values.isDetection,
  travelThirdParty: values.isLiable,
  luggage: values.isCharger,
  extreme: values.isEsports,
  winterExtreme: values.isWsports,
  cancellation: values.isTravelc,
  shortening: values.isTravelsc,
  pcAndTablet: values.isPccover,
  mobile: values.isMobilecover,
  cancellationOfSelfParticipationOnRentedCar: values.isCancellationOfSelfParticipationOnRentedCar,
} : null;

export function SidebarCoveragePanel({
  formikValues,
  quotedFormValues,
  formikSetField,
  formikErrors,
  validateForm,
  onUpdateRiders,
  onRidersChange,
}) {
  const intl = useIntl();
  const theme = useTheme();

  const [isCharger, setIsCharger] = React.useState(Boolean(formikValues.isCharger));
  const [isEsports, setIsEsports] = React.useState(Boolean(formikValues.isEsports));
  const [isWsports, setIsWsports] = React.useState(Boolean(formikValues.isWsports));
  const [isTravelsc, setIsTravelsc] = React.useState(Boolean(formikValues.isTravelsc));
  const [isPccover, setIsPccover] = React.useState(Boolean(formikValues.isPccover));
  const [isMobilecover, setIsMobilecover] = React.useState(Boolean(formikValues.isMobilecover));
  const [isCancellationOfSelfParticipationOnRentedCar, setIsCancellationOfSelfParticipationOnRentedCar] = React.useState(Boolean(formikValues.isCancellationOfSelfParticipationOnRentedCar));
  const hasRiderChanges = !isEqual(
    getRiderCoverageValues(formikValues),
    getRiderCoverageValues(quotedFormValues || formikValues),
  );
  const [isRidersChange, setIsRidersChange] = React.useState(hasRiderChanges);
  const [isHospitalizationModalOpen, setIsHospitalizationModalOpen] = React.useState(false);

  const [baseOpen, setBaseOpen] = React.useState(false);

  React.useEffect(() => {
    setIsRidersChange(hasRiderChanges);
    onRidersChange?.(hasRiderChanges);
  }, [hasRiderChanges, onRidersChange]);

  const handleHospitalizationModalClose = (removeCoverage) => {
    if (removeCoverage) {
      setIsTravelsc(false);
      formikSetField('isTravelsc', false);
      formikSetField('isOwnerHospital2', '');
    }
    setIsHospitalizationModalOpen(false);
  };

  const convertToUTCDate = (momentDate) => {
    return new Date(Date.UTC(momentDate.year(), momentDate.month(), momentDate.date(), 12, 0, 0));
  };

  const calculateDaysChange = (startDate, endDate) => {
    if (!startDate.isValid() || !endDate.isValid()) return 0;
    return endDate.diff(startDate, 'days') + 1;
  };

  const updateSportDateRange = (traveler, type, isStart, utcDate, date) => {
    const rangeKey = type === 'extremeSport' ? 'dateExtremeSportRange' : 'dateWinterSportRange';
    const startKey = type === 'extremeSport' ? 'startDateExtremeSport' : 'startDateWinterSport';
    const endKey = type === 'extremeSport' ? 'endDateExtremeSport' : 'endDateWinterSport';
    const range = traveler[rangeKey] || {};
    const update = isStart
      ? {[startKey]: utcDate, ...(range[endKey] && date.isAfter(range[endKey]) ? {[endKey]: null} : {})}
      : {[endKey]: utcDate};
    return updateNestedTraveler(formikValues.travelers, traveler, rangeKey, update);
  };

  const updateDatePicker = (type, StartOrEnd, dateinput, traveler) => {
    let date = moment(dateinput);
    const isStart = StartOrEnd === 'start';
    if (!date.isValid()) {
      formikSetField('travelers', updateSportDateRange(traveler, type, isStart, null, date));
      setIsRidersChange(true);
      return;
    }
    if (StartOrEnd === 'start' && (
      date.isBefore(moment(formikValues.policyStartDate), 'day') ||
      date.isAfter(moment(formikValues.policyEndDate), 'day')
    )) {
      date = moment(formikValues.policyStartDate);
    } else if (StartOrEnd === 'end' && (
      date.isAfter(moment(formikValues.policyEndDate), 'day') ||
      date.isBefore(moment(formikValues.policyStartDate), 'day')
    )) {
      date = moment(formikValues.policyEndDate);
    }
    const utcDate = convertToUTCDate(date);
    if (type === 'extremeSport' || type === 'winterSport') {
      formikSetField('travelers', updateSportDateRange(traveler, type, isStart, utcDate, date));
    }
    setIsRidersChange(true);
  };

  const clickDateExtremeSport = (traveler, value) => {
    const range = traveler.dateExtremeSportRange || {};
    formikSetField('travelers', updateTraveler(formikValues.travelers, traveler, {
      needDateExtremeSport: value,
      dateExtremeSportRange: value ? {
        ...range,
        startDateExtremeSport: range.startDateExtremeSport || convertToUTCDate(moment(formikValues.policyStartDate)),
        endDateExtremeSport: range.endDateExtremeSport || convertToUTCDate(moment(formikValues.policyEndDate)),
      } : {},
    }));
  };

  const clickDateWinterSport = (traveler, value) => {
    const range = traveler.dateWinterSportRange || {};
    formikSetField('travelers', updateTraveler(formikValues.travelers, traveler, {
      needDateWinterSport: value,
      dateWinterSportRange: value ? {
        ...range,
        startDateWinterSport: range.startDateWinterSport || convertToUTCDate(moment(formikValues.policyStartDate)),
        endDateWinterSport: range.endDateWinterSport || convertToUTCDate(moment(formikValues.policyEndDate)),
      } : {},
    }));
  };

  const clickPcCoverage = (traveler, value) => {
    formikSetField('travelers', updateTraveler(formikValues.travelers, traveler, {
      needPcCoverage: value,
      pc: value ? {...(traveler.pc || {})} : {},
    }));
  };

  const clickMobileCoverage = (traveler, value) => {
    formikSetField('travelers', updateTraveler(formikValues.travelers, traveler, {
      needMobileCoverage: value,
      mobile: value ? {...(traveler.mobile || {})} : {},
    }));
  };

  const clickCancellationOfSelfParticipationOnRentedCar = (traveler, value) => {
    formikSetField('travelers', selectExclusiveTraveler(
      formikValues.travelers,
      traveler,
      'cancellationOfSelfParticipationOnRentedCar',
      value,
    ));
  };

  const handleDeviceUpdate = (index, key, itemKey, value) => {
    formikSetField('travelers', updateNestedTraveler(
      formikValues.travelers, index, itemKey, {[key]: value},
    ));
    setIsRidersChange(true);
  };

  const renderDatePicker = (traveler, type) => {
    if (!traveler) return null;
    const minDate = toIsoDateValue(formikValues.policyStartDate);
    const maxDate = toIsoDateValue(formikValues.policyEndDate);

    if (type === 'extremeSport') {
      if (!traveler.needDateExtremeSport) return null;
      const startIso = toIsoDateValue(traveler.dateExtremeSportRange?.startDateExtremeSport);
      const endIso = toIsoDateValue(traveler.dateExtremeSportRange?.endDateExtremeSport);
      return (
        <div className='sidebar-coverage__date-picker'>
          <TripDatePicker
            selectionMode='independent'
            startValue={startIso} endValue={endIso}
            minDate={minDate} maxStartDate={maxDate} maxEndDate={maxDate}
            popupStrategy='viewport'
            onStartChange={(iso) => updateDatePicker('extremeSport', 'start', iso, traveler)}
            onEndChange={(iso) => iso && updateDatePicker('extremeSport', 'end', iso, traveler)}
            startLabel={intl.formatMessage({id: 'coverage.startDate'})}
            endLabel={intl.formatMessage({id: 'coverage.endDate'})}
          />
          {startIso && endIso && (
            <div style={{textAlign: 'center', fontSize: theme.typography.pxToRem(12.5), color: theme.palette.text.primary, marginTop: 8}}>
              <FormattedMessage id='step1.tab1.days_num_winter_extreme_sports' values={{
                number: <b>{calculateDaysChange(moment(startIso), moment(endIso))}</b>,
              }} />
            </div>
          )}
        </div>
      );
    }

    if (!traveler.needDateWinterSport) return null;
    const startIso = toIsoDateValue(traveler.dateWinterSportRange?.startDateWinterSport);
    const endIso = toIsoDateValue(traveler.dateWinterSportRange?.endDateWinterSport);
    return (
      <div className='sidebar-coverage__date-picker'>
        <TripDatePicker
          selectionMode='independent'
          startValue={startIso} endValue={endIso}
          minDate={minDate} maxStartDate={maxDate} maxEndDate={maxDate}
          popupStrategy='viewport'
          onStartChange={(iso) => updateDatePicker('winterSport', 'start', iso, traveler)}
          onEndChange={(iso) => iso && updateDatePicker('winterSport', 'end', iso, traveler)}
          startLabel={intl.formatMessage({id: 'coverage.startDate'})}
          endLabel={intl.formatMessage({id: 'coverage.endDate'})}
        />
        {startIso && endIso && (
          <div style={{textAlign: 'center', fontSize: theme.typography.pxToRem(12.5), color: theme.palette.text.primary, marginTop: 8}}>
            <FormattedMessage id='step1.tab1.days_num_winter_extreme_sports' values={{
              number: <b>{calculateDaysChange(moment(startIso), moment(endIso))}</b>,
            }} />
          </div>
        )}
      </div>
    );
  };

  const markChanged = (fn) => (...args) => { setIsRidersChange(true); fn(...args); };

  const panelStyle = {
    background: theme.palette.background.paper,
    borderRadius: 18,
    border: `1px solid ${theme.palette.divider}`,
    boxShadow: `0 4px 18px ${alpha(theme.palette.secondary.dark, 0.06)}`,
    overflow: 'hidden',
  };

  const headerStyle = {
    padding: '15px 18px 13px',
    borderBottom: `1px solid ${theme.palette.divider}`,
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    fontWeight: theme.typography.fontWeightBold,
    fontSize: theme.typography.pxToRem(12.5),
    color: theme.palette.text.primary,
    lineHeight: 1.35,
  };

  const headerIconStyle = {
    width: 32, height: 32, flex: 'none',
    borderRadius: '50%', background: theme.palette.action.selected, color: theme.palette.secondary.main,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  };

  const sectionLabelStyle = {
    fontSize: theme.typography.pxToRem(12), fontWeight: theme.typography.fontWeightBold, letterSpacing: '.02em',
    color: theme.palette.text.secondary, padding: '10px 16px 4px',
  };

  const updateBtnStyle = {
    width: '100%', height: 46, border: 'none', cursor: 'pointer',
    background: theme.palette.primary.main, color: theme.palette.primary.contrastText, borderRadius: 9999,
    fontFamily: 'inherit', fontWeight: theme.typography.fontWeightBold, fontSize: theme.typography.pxToRem(14),
    boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
    margin: '12px 0 4px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
    opacity: isRidersChange ? 1 : 0.6,
  };

  return (
    <div style={panelStyle}>
      <div style={headerStyle}>
        <span style={headerIconStyle}>
          <Ic name="shield" size={17} />
        </span>
        <span data-testid='coverage-panel-heading'><FormattedMessage id='coverage.selectionInstruction'/></span>
      </div>

      <div style={{padding: '2px 16px 0', borderBottom: `1px solid ${theme.palette.divider}`}}>
        <button
          type="button"
          onClick={() => setBaseOpen((o) => !o)}
          style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', background: 'none', border: 'none', cursor: 'pointer', padding: '12px 2px 6px', fontSize: theme.typography.pxToRem(12), fontWeight: theme.typography.fontWeightBold, letterSpacing: '.02em', color: theme.palette.text.secondary, fontFamily: 'inherit'}}
        >
          <span><FormattedMessage id='coverage.baseIncluded'/></span>
          <span style={{display: 'inline-flex', color: theme.palette.secondary.dark, transform: baseOpen ? 'rotate(180deg)' : 'none', transition: 'transform .18s'}}><Ic name="chevron" size={15} /></span>
        </button>
        {baseOpen && (
          <>
            <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 9, padding: '6px 2px', fontSize: theme.typography.pxToRem(13), color: theme.palette.text.primary, textAlign: 'right'}}>
              <span style={{display: 'flex', alignItems: 'center', gap: 9, minWidth: 0}}>
                <span style={{width: 18, height: 18, borderRadius: 5, background: theme.palette.action.selected, color: theme.palette.secondary.main, display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 'none'}}><Ic name="check" size={12} /></span>
                <FormattedMessage id='coverage.medicalTitle'/>
              </span>
              <span style={{flex: 'none', fontSize: theme.typography.pxToRem(11.5), color: theme.palette.text.secondary, whiteSpace: 'nowrap'}}>
                <FormattedMessage id='coverage.nonRemovable'/>
              </span>
            </div>
            {[
              {field: 'isDetection', titleId: 'riders.locateAndExtract', price: '0.2'},
              {field: 'isLiable', titleId: 'coverage.liabilityName', price: '0.03'},
            ].map(({field, titleId, price}) => {
              const checked = Boolean(formikValues[field]);
              return (
                <button
                  key={field}
                  type='button'
                  role='checkbox'
                  aria-checked={checked}
                  aria-label={intl.formatMessage({id: titleId})}
                  onClick={() => {
                    formikSetField(field, !checked);
                    setIsRidersChange(true);
                  }}
                  style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', gap: 9, padding: '6px 2px', border: 'none', background: 'none', cursor: 'pointer', fontFamily: 'inherit', fontSize: theme.typography.pxToRem(13), color: checked ? theme.palette.text.primary : theme.palette.text.secondary, textAlign: 'right'}}
                >
                  <span style={{display: 'flex', alignItems: 'center', gap: 9, minWidth: 0}}>
                    <span aria-hidden='true' style={{width: 18, height: 18, boxSizing: 'border-box', borderRadius: 5, border: checked ? 'none' : `2px solid ${theme.palette.text.disabled}`, background: checked ? theme.palette.action.selected : theme.palette.background.paper, color: theme.palette.secondary.main, display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 'none'}}>
                      {checked && <Ic name="check" size={12} />}
                    </span>
                    <span style={{textDecoration: checked ? 'none' : 'line-through'}}>
                      <FormattedMessage id={titleId}/>
                    </span>
                  </span>
                  <span style={{flex: 'none', fontSize: theme.typography.pxToRem(11.5), color: theme.palette.text.secondary, whiteSpace: 'nowrap'}}>
                    {checked ? <FormattedMessage id='coverage.dailyPrice' values={{price}}/> : <FormattedMessage id='coverage.removed'/>}
                  </span>
                </button>
              );
            })}
          </>
        )}
      </div>

      <div style={sectionLabelStyle}><FormattedMessage id='coverage.extensionsTitle'/></div>

      <div style={{padding: '0 16px 14px'}} data-panel-extensions>
        <style>{`
          [data-panel-extensions] .rd-dates-grid {
            grid-template-columns: 1fr !important;
            gap: 8px !important;
          }
          [data-panel-extensions] [style*="gap: 12px"][style*="grid"] {
            grid-template-columns: 1fr !important;
            gap: 8px !important;
          }
          [data-panel-extensions] .sidebar-coverage__date-picker input[data-testid^="date-picker-"] {
            font-weight: 700 !important;
            text-align: center !important;
          }
          [data-panel-extensions] .sidebar-coverage__date-picker input[data-testid^="date-picker-"] + button {
            position: absolute !important;
            inset-block-start: 7px;
            inset-inline-start: 10px;
          }
          [data-panel-extensions] .sidebar-coverage__binary-choice label {
            border-radius: 18px !important;
          }
          .offers-hospitalization-dialog {
            z-index: 1400 !important;
          }
        `}</style>

        {/* Luggage */}
        <CoverageExtensionRow
          compact
          icon={RIDER_DISPLAY.luggage.icon}
          title={intl.formatMessage({id: RIDER_DISPLAY.luggage.titleId})}
          price={RIDER_DISPLAY.luggage.price}
          recommended={intl.formatMessage({id: 'coverage.recommended'})}
          checked={formikValues[RIDER_DISPLAY.luggage.formikKey]}
          onToggle={() => {
            const next = !formikValues[RIDER_DISPLAY.luggage.formikKey];
            setIsCharger(next);
            formikSetField(RIDER_DISPLAY.luggage.formikKey, next);
            setIsRidersChange(true);
          }}
          testId={RIDER_DISPLAY.luggage.testId}
        />

        {/* Extreme sport */}
        <CoverageExtensionRow
          compact
          icon={RIDER_DISPLAY.extremeSport.icon}
          title={intl.formatMessage({id: RIDER_DISPLAY.extremeSport.titleId})}
          price={RIDER_DISPLAY.extremeSport.price}
          checked={formikValues[RIDER_DISPLAY.extremeSport.formikKey]}
          onToggle={() => {
            const next = !formikValues[RIDER_DISPLAY.extremeSport.formikKey];
            if (formikValues.travelers.length === 1) {
              clickDateExtremeSport(formikValues.travelers[0], next);
            }
            if (!next && formikValues.travelers.length > 1) {
              formikSetField('travelers', updateAllTravelers(formikValues.travelers, {
                needDateExtremeSport: false,
                dateExtremeSportRange: {},
              }));
            }
            setIsEsports(next);
            formikSetField(RIDER_DISPLAY.extremeSport.formikKey, next);
            setIsRidersChange(true);
          }}
          testId={RIDER_DISPLAY.extremeSport.testId}
        >
          {formikValues.travelers.length > 1 && formikValues[RIDER_DISPLAY.extremeSport.formikKey] && formikValues.travelers.map((traveler, i) => (
            traveler.gender === 1 ? null : (
              <div key={`t-${i}-ext`} style={{marginTop: 8}}>
                <FormControlLabel
                  label={traveler.fullName}
                  control={<MuiCheckbox
                    checked={Boolean(traveler.needDateExtremeSport)}
                    onChange={(e, v) => { clickDateExtremeSport(traveler, v); setIsRidersChange(true); }}
                    size="small"
                  />}
                />
                {renderDatePicker(traveler, 'extremeSport')}
              </div>
            )
          ))}
          {formikValues.travelers.length === 1 && renderDatePicker(formikValues.travelers[0], 'extremeSport')}
        </CoverageExtensionRow>

        {/* Winter sport */}
        <CoverageExtensionRow
          compact
          icon={RIDER_DISPLAY.winterSport.icon}
          title={intl.formatMessage({id: RIDER_DISPLAY.winterSport.titleId})}
          price={RIDER_DISPLAY.winterSport.price}
          checked={formikValues[RIDER_DISPLAY.winterSport.formikKey]}
          onToggle={() => {
            const next = !formikValues[RIDER_DISPLAY.winterSport.formikKey];
            if (formikValues.travelers.length === 1) {
              clickDateWinterSport(formikValues.travelers[0], next);
            } else if (!next) {
              formikSetField('travelers', updateAllTravelers(formikValues.travelers, {
                needDateWinterSport: false,
                dateWinterSportRange: {},
              }));
            }
            setIsWsports(next);
            formikSetField(RIDER_DISPLAY.winterSport.formikKey, next);
            setIsRidersChange(true);
          }}
          testId={RIDER_DISPLAY.winterSport.testId}
        >
          {formikValues.travelers.length > 1 && formikValues[RIDER_DISPLAY.winterSport.formikKey] && formikValues.travelers.map((traveler, i) => (
            traveler.gender === 1 ? null : (
              <div key={`t-${i}-win`} style={{marginTop: 8}}>
                <FormControlLabel
                  label={traveler.fullName}
                  control={<MuiCheckbox
                    checked={Boolean(traveler.needDateWinterSport)}
                    onChange={(e, v) => { clickDateWinterSport(traveler, v); setIsRidersChange(true); }}
                    size="small"
                  />}
                />
                {renderDatePicker(traveler)}
              </div>
            )
          ))}
          {formikValues.travelers.length === 1 && renderDatePicker(formikValues.travelers[0])}
        </CoverageExtensionRow>

        {/* Cancellation / shortening */}
        <CoverageExtensionRow
          compact
          icon={RIDER_DISPLAY.cancellation.icon}
          title={intl.formatMessage({id: RIDER_DISPLAY.cancellation.titleId})}
          price={RIDER_DISPLAY.cancellation.price}
          checked={formikValues[RIDER_DISPLAY.cancellation.formikKey]}
          onToggle={() => {
            const next = !formikValues[RIDER_DISPLAY.cancellation.formikKey];
            setIsTravelsc(next);
            formikSetField(RIDER_DISPLAY.cancellation.formikKey, next);
            formikSetField('isOwnerHospital2', '');
            setIsRidersChange(true);
          }}
          testId={RIDER_DISPLAY.cancellation.testId}
        >
          {formikValues[RIDER_DISPLAY.cancellation.formikKey] && isTravelsc && (
            <div style={{marginTop: 8}}>
              <div style={{fontSize: theme.typography.pxToRem(12.5), fontWeight: theme.typography.fontWeightSemiBold, color: theme.palette.text.primary, lineHeight: 1.5, textAlign: 'right'}}>
                {intl.formatMessage({id: 'step1.tab5.travelc_bottom2_ask'})}
              </div>
              <div style={{marginTop: 8}}>
                <SegmentedControl
                  className='sidebar-coverage__binary-choice'
                  ariaLabel={intl.formatMessage({id: 'step1.tab5.travelc_bottom2_ask'})}
                  value={formikValues.isOwnerHospital2}
                  options={[
                    {value: 'yes', label: intl.formatMessage({id: 'yes'})},
                    {value: 'no', label: intl.formatMessage({id: 'no'})},
                  ]}
                  onChange={(val, event) => {
                    event.stopPropagation();
                    formikSetField('isOwnerHospital2', val);
                    setIsRidersChange(true);
                    if (val === 'yes') setIsHospitalizationModalOpen(true);
                  }}
                />
                {formikErrors.isOwnerHospital2 && (
                  <div data-testid='isOwnerHospital2Error' style={{fontSize: theme.typography.pxToRem(12), color: theme.palette.error.main, marginTop: 4}}>{formikErrors.isOwnerHospital2}</div>
                )}
              </div>
            </div>
          )}
        </CoverageExtensionRow>

        {/* PC / Tablet */}
        <CoverageExtensionRow
          compact
          icon={RIDER_DISPLAY.pc.icon}
          title={intl.formatMessage({id: RIDER_DISPLAY.pc.titleId})}
          price={RIDER_DISPLAY.pc.price}
          checked={formikValues[RIDER_DISPLAY.pc.formikKey]}
          onToggle={() => {
            const next = !formikValues[RIDER_DISPLAY.pc.formikKey];
            setIsPccover(next);
            if (formikValues.travelers.length === 1) {
              clickPcCoverage(formikValues.travelers[0], next);
            } else if (!next) {
              formikSetField('travelers', updateAllTravelers(formikValues.travelers, {
                needPcCoverage: false,
                pc: {},
              }));
            }
            formikSetField(RIDER_DISPLAY.pc.formikKey, next);
            setIsRidersChange(true);
          }}
          testId={RIDER_DISPLAY.pc.testId}
        >
          {isPccover && formikValues.travelers.map((traveler, i) => (
            traveler.gender === 1 ? null : (
              <div key={`t-${i}-pc`}>
                {formikValues.travelers.length > 1 && (
                  <FormControlLabel
                    label={traveler.fullName}
                    control={<MuiCheckbox
                      checked={Boolean(traveler.needPcCoverage)}
                      onChange={(e, v) => { clickPcCoverage(traveler, v); setIsRidersChange(true); }}
                      size="small"
                    />}
                  />
                )}
                {traveler.needPcCoverage && traveler.pc && (
                  <DeviceFields traveler={traveler} index={i} formikErrors={formikErrors} onUpdate={handleDeviceUpdate} showYear />
                )}
              </div>
            )
          ))}
        </CoverageExtensionRow>

        {/* Mobile phone */}
        <CoverageExtensionRow
          compact
          icon={RIDER_DISPLAY.mobile.icon}
          title={intl.formatMessage({id: RIDER_DISPLAY.mobile.titleId})}
          price={RIDER_DISPLAY.mobile.price}
          checked={formikValues[RIDER_DISPLAY.mobile.formikKey]}
          onToggle={() => {
            const next = !formikValues[RIDER_DISPLAY.mobile.formikKey];
            setIsMobilecover(next);
            if (formikValues.travelers.length === 1) {
              clickMobileCoverage(formikValues.travelers[0], next);
            } else if (!next) {
              formikSetField('travelers', updateAllTravelers(formikValues.travelers, {
                needMobileCoverage: false,
                mobile: {},
              }));
            }
            formikSetField(RIDER_DISPLAY.mobile.formikKey, next);
            setIsRidersChange(true);
          }}
          testId={RIDER_DISPLAY.mobile.testId}
          last={!RIDER_DISPLAY.rentalCar.isEligible(formikValues.travelers)}
        >
          {isMobilecover && formikValues.travelers.map((traveler, i) => (
            traveler.gender === 1 ? null : (
              <div key={`t-${i}-mob`}>
                {formikValues.travelers.length > 1 && (
                  <FormControlLabel
                    label={traveler.fullName}
                    control={<MuiCheckbox
                      checked={Boolean(traveler.needMobileCoverage)}
                      onChange={(e, v) => { clickMobileCoverage(traveler, v); setIsRidersChange(true); }}
                      size="small"
                    />}
                  />
                )}
                {traveler.needMobileCoverage && traveler.mobile && (
                  <DeviceFields traveler={traveler} index={i} formikErrors={formikErrors} onUpdate={handleDeviceUpdate} showYear={false} />
                )}
              </div>
            )
          ))}
        </CoverageExtensionRow>

        {/* Rental car */}
        {RIDER_DISPLAY.rentalCar.isEligible(formikValues.travelers) && (
          <CoverageExtensionRow
            compact
            icon={RIDER_DISPLAY.rentalCar.icon}
            title={intl.formatMessage({id: RIDER_DISPLAY.rentalCar.titleId})}
            price={RIDER_DISPLAY.rentalCar.price}
            checked={formikValues[RIDER_DISPLAY.rentalCar.formikKey]}
            onToggle={() => {
              const next = !formikValues[RIDER_DISPLAY.rentalCar.formikKey];
              setIsCancellationOfSelfParticipationOnRentedCar(next);
              let singleIdx;
              for (let i = 0; i < formikValues.travelers.length; i++) {
                if (RIDER_DISPLAY.rentalCar.isTravelerEligible(formikValues.travelers[i])) {
                  if (singleIdx !== undefined) { singleIdx = undefined; break; }
                  else { singleIdx = i; }
                }
              }
              if (formikValues.travelers.length === 1) {
                clickCancellationOfSelfParticipationOnRentedCar(formikValues.travelers[0], next);
              } else if (singleIdx !== undefined) {
                clickCancellationOfSelfParticipationOnRentedCar(formikValues.travelers[singleIdx], next);
              }
              if (!next) {
                formikSetField('travelers', updateAllTravelers(formikValues.travelers, {
                  cancellationOfSelfParticipationOnRentedCar: false,
                }));
              }
              formikSetField(RIDER_DISPLAY.rentalCar.formikKey, next);
              setIsRidersChange(true);
            }}
            testId={RIDER_DISPLAY.rentalCar.testId}
            last
          >
            {isCancellationOfSelfParticipationOnRentedCar && formikValues.travelers.length > 1 && (
              <div style={{marginTop: 8}}>
                <p style={{lineHeight: 1.5, marginBottom: 4, fontSize: theme.typography.pxToRem(12.5), color: theme.palette.text.primary}}>
                  {intl.formatMessage({id: 'step1.tab5.cancellationOfSelfParticipationOnRentedCar_explain_what_todo'})}
                </p>
                {formikValues.travelers.map((traveler, i) => (
                  !RIDER_DISPLAY.rentalCar.isTravelerEligible(traveler) ? null : (
                    <FormControlLabel
                      key={`t-${i}-car`}
                      label={traveler.fullName}
                      control={<MuiCheckbox
                        checked={Boolean(traveler.cancellationOfSelfParticipationOnRentedCar)}
                        onChange={(e, v) => { clickCancellationOfSelfParticipationOnRentedCar(traveler, v); setIsRidersChange(true); }}
                        size="small"
                      />}
                    />
                  )
                ))}
              </div>
            )}
          </CoverageExtensionRow>
        )}

        <button
          type="button"
          style={updateBtnStyle}
          disabled={!isRidersChange}
          onClick={async () => {
            const errors = await validateForm();
            if (Object.keys(errors).length === 0) {
              const originalData = toCoverageSelection(quotedFormValues);
              const currentData = toCoverageSelection(formikValues);
              const baseline = originalData || currentData;
              onUpdateRiders();
              fireGtmEvent('UpdateCoverages', {
                originalData,
                updatedData: Object.fromEntries(Object.entries(currentData)
                  .filter(([coverage, value]) => Boolean(value) !== Boolean(baseline[coverage]))
                  .map(([coverage, value]) => [coverage, Boolean(value)])),
              });
            }
          }}
        >
          <Ic name="shield" size={20} />
          <FormattedMessage id='offers.coverage.update' />
        </button>
        <Typography sx={{color: theme.palette.text.secondary, fontSize: theme.typography.pxToRem(13), textAlign: 'center', marginBottom: 1}}>
          <FormattedMessage id='offers.coverage.updateHint' />
        </Typography>
      </div>

      <Dialog
        className='offers-hospitalization-dialog'
        open={isHospitalizationModalOpen}
        onClose={() => handleHospitalizationModalClose(true)}
        title={intl.formatMessage({id: 'step1.tab5.travelc_bottom2_notation'})}
        width={600}
        footer={<>
          <Button
            data-testid='cancel-cover-btn-modal'
            variant='secondary'
            onClick={() => handleHospitalizationModalClose(true)}
          >
            <FormattedMessage id='cancel'/>
          </Button>
          <Button
            data-testid='add-cover-btn-modal'
            variant='accent'
            onClick={() => handleHospitalizationModalClose(false)}
          >
            <FormattedMessage id='coverage.notice.add_cover'/>
          </Button>
        </>}
        style={{textAlign: 'right'}}
      >
        <p style={{fontSize: theme.typography.pxToRem(15), lineHeight: 1.6, color: theme.palette.text.secondary, margin: 0}}>
          {intl.formatMessage({id: 'step1.tab5.travelc_bottom2_notation2'})}
        </p>
      </Dialog>
    </div>
  );
}
